import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { CloudUpload, Warning, Delete } from "@mui/icons-material";
import { db } from "../firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";

function ProductDelete({ open, onClose, onDeleteComplete, categories }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [previewMode, setPreviewMode] = useState(true);
  const [productsToDelete, setProductsToDelete] = useState([]);
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const fileName = selectedFile.name.toLowerCase();
      
      if (
        fileType === "text/csv" ||
        fileName.endsWith(".csv") ||
        fileType === "application/json" ||
        fileName.endsWith(".json")
      ) {
        setFile(selectedFile);
        setError("");
        setResults(null);
        setProductsToDelete([]);
        setPreviewMode(true);
        setConfirmationChecked(false);
      } else {
        setError("Please select a CSV or JSON file");
      }
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    const headers = lines[0].split(",").map(h => h.trim());
    
    return lines.slice(1).map((line, index) => {
      // Skip empty lines
      if (!line.trim()) return null;
      
      const values = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
      const obj = {};
      
      headers.forEach((header, headerIndex) => {
        let value = values[headerIndex] || "";
        
        if (header === "price") {
          obj[header] = parseFloat(value) || 0;
        } else if (header === "aisle" || header === "rack_level") {
          obj[header] = parseInt(value, 10) || 0;
        } else if (header === "keywords") {
          obj[header] = value ? value.split(";").map(k => k.trim()).filter(Boolean) : [];
        } else {
          obj[header] = value;
        }
      });
      
      return obj;
    }).filter(product => product !== null && product.name && product.name.trim() !== "");
  };

  const validateProductForDeletion = (product, index) => {
    if (!product.name || product.name.trim() === "") {
      return `Missing product name`;
    }
    
    if (!product.barcode || product.barcode.trim() === "") {
      return `Missing barcode`;
    }
    
    return null;
  };

  const findProductsToDelete = async (csvProducts) => {
    const existing = await getDocs(collection(db, "products"));
    const existingProducts = existing.docs.map(d => ({ id: d.id, ...d.data() }));
    const foundProducts = [];
    const notFoundProducts = [];
    
    csvProducts.forEach((csvProduct, index) => {
      const validationError = validateProductForDeletion(csvProduct, index);
      
      if (validationError) {
        notFoundProducts.push(`Row ${index + 1}: ${validationError}`);
        return;
      }
      
      // Find exact match: BOTH name AND barcode must match
      const matchingProduct = existingProducts.find(existing => 
        existing.name?.toLowerCase().trim() === csvProduct.name?.toLowerCase().trim() &&
        existing.barcode?.trim() === csvProduct.barcode?.trim()
      );
      
      if (matchingProduct) {
        foundProducts.push({
          ...matchingProduct,
          csvRowIndex: index + 1
        });
      } else {
        notFoundProducts.push(`Row ${index + 1}: Product "${csvProduct.name}" with barcode "${csvProduct.barcode}" not found in database`);
      }
    });
    
    return { foundProducts, notFoundProducts };
  };

  const handlePreview = async () => {
    if (!file) {
      setError("Please select a file");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const text = await file.text();
      let csvProducts = [];
      
      if (file.name.toLowerCase().endsWith(".json")) {
        csvProducts = JSON.parse(text);
      } else {
        csvProducts = parseCSV(text);
      }

      if (!Array.isArray(csvProducts)) {
        throw new Error("File must contain an array of products");
      }

      const { foundProducts, notFoundProducts } = await findProductsToDelete(csvProducts);
      
      setProductsToDelete(foundProducts);
      setResults({
        total: csvProducts.length,
        found: foundProducts.length,
        notFound: notFoundProducts.length,
        notFoundErrors: notFoundProducts,
      });
      
      setPreviewMode(false);

    } catch (err) {
      setError(`Preview failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmationChecked) {
      setError("Please confirm that you understand this action cannot be undone");
      return;
    }

    if (productsToDelete.length === 0) {
      setError("No products to delete");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      let successCount = 0;
      let failedCount = 0;
      const failedDeletions = [];

      for (const product of productsToDelete) {
        try {
          await deleteDoc(doc(db, "products", product.id));
          successCount++;
        } catch (err) {
          failedCount++;
          failedDeletions.push(`${product.name}: ${err.message}`);
        }
      }

      setResults({
        ...results,
        deleted: successCount,
        deleteFailed: failedCount,
        deleteErrors: failedDeletions,
        completed: true
      });

      if (successCount > 0) {
        onDeleteComplete?.();
      }

    } catch (err) {
      setError(`Deletion failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError("");
    setResults(null);
    setProductsToDelete([]);
    setPreviewMode(true);
    setConfirmationChecked(false);
    onClose();
  };

  const canProceedToDelete = results && !results.completed && productsToDelete.length > 0;
  const showDeleteResults = results && results.completed;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Warning />
        Delete Products from CSV
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">⚠️ DANGEROUS OPERATION</Typography>
          <Typography variant="body2">
            This will permanently delete products from your database. This action cannot be undone.
            Only products with EXACT name AND barcode matches will be deleted.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload the same CSV file used for import to delete those products.
            The system will find products where both name and barcode match exactly.
          </Typography>
          
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUpload />}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            Choose File
            <input
              type="file"
              accept=".csv,.json"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </Button>
          
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {file.name}
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{error}</pre>
          </Alert>
        )}

        {results && !results.completed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Preview Results:</Typography>
            <Typography variant="body2">
              • Total records in CSV: {results.total}
            </Typography>
            <Typography variant="body2">
              • Products found for deletion: {results.found}
            </Typography>
            <Typography variant="body2">
              • Products not found: {results.notFound}
            </Typography>
            
            {results.notFoundErrors && results.notFoundErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="warning.main">
                  Products not found in database:
                </Typography>
                <pre style={{ fontSize: "0.75rem", margin: 0, maxHeight: 200, overflow: 'auto' }}>
                  {results.notFoundErrors.slice(0, 10).join("\n")}
                  {results.notFoundErrors.length > 10 && `\n... and ${results.notFoundErrors.length - 10} more`}
                </pre>
              </Box>
            )}
          </Alert>
        )}

        {showDeleteResults && (
          <Alert severity={results.deleteFailed === 0 ? "success" : "warning"} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Deletion Results:</Typography>
            <Typography variant="body2">
              • Products successfully deleted: {results.deleted}
            </Typography>
            {results.deleteFailed > 0 && (
              <Typography variant="body2">
                • Failed deletions: {results.deleteFailed}
              </Typography>
            )}
            
            {results.deleteErrors && results.deleteErrors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="error">
                  Deletion errors:
                </Typography>
                <pre style={{ fontSize: "0.75rem", margin: 0 }}>
                  {results.deleteErrors.join("\n")}
                </pre>
              </Box>
            )}
          </Alert>
        )}

        {canProceedToDelete && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" color="error.main" sx={{ mb: 2 }}>
              Confirm Deletion
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              The following {productsToDelete.length} products will be permanently deleted:
            </Typography>
            
            <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2, border: 1, borderColor: 'grey.300', p: 1 }}>
              {productsToDelete.slice(0, 20).map((product, index) => (
                <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem' }}>
                  • {product.name} (Barcode: {product.barcode})
                </Typography>
              ))}
              {productsToDelete.length > 20 && (
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                  ... and {productsToDelete.length - 20} more products
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  color="error"
                />
              }
              label="I understand this will permanently delete these products and cannot be undone"
            />
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography>
              {previewMode ? "Analyzing CSV..." : "Deleting products..."}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          {showDeleteResults ? "Close" : "Cancel"}
        </Button>
        
        {previewMode && (
          <Button
            onClick={handlePreview}
            variant="contained"
            disabled={!file || loading}
          >
            {loading ? "Analyzing..." : "Preview Deletions"}
          </Button>
        )}
        
        {canProceedToDelete && (
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading || !confirmationChecked}
            startIcon={<Delete />}
          >
            {loading ? "Deleting..." : `Delete ${productsToDelete.length} Products`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ProductDelete;